import { IconFolder } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import NSModal from 'bricks/NSModal/NSModal';
import { NSButton, NSCard, NSCheckbox } from 'bricks';
import { IDealDirectory } from 'views/DocumentLibrary/types';
import { getFileIcon } from 'views/DocumentLibrary/helpers';
import { flattenDocumentsFromTree } from '../../../../helpers';
import './PickFromDocumentsModal.scss';

interface PickFromDocumentsModalProps {
    isOpen: boolean;
    toggle: () => void;
    directoryTree: IDealDirectory | null;
    attachedDocumentIds: Set<number>;
    onConfirm: (selectedDocumentIds: number[]) => void;
}

const PickFromDocumentsModal: React.FC<PickFromDocumentsModalProps> = ({
    isOpen,
    toggle,
    directoryTree,
    attachedDocumentIds,
    onConfirm,
}) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const availableDocuments = useMemo(() => {
        if (!directoryTree) return [];
        const allDocs = flattenDocumentsFromTree(directoryTree);
        return allDocs.filter(doc => !attachedDocumentIds.has(doc.id));
    }, [directoryTree, attachedDocumentIds]);

    const handleToggleDocument = (docId: number) => {
        setSelectedIds(prev => {
            const next = [...prev];
            if (next.includes(docId)) {
                return next.filter(id => id !== docId);
            } else {
                next.push(docId);
            }
            return next;
        });
    };

    const handleConfirm = () => {
        onConfirm(selectedIds);
        setSelectedIds([]);
        toggle();
    };

    const handleCancel = () => {
        setSelectedIds([]);
        toggle();
    };

    return (
        <NSModal
            isOpen={isOpen}
            toggle={handleCancel}
            modalHeaderText="Pick from Documents"
            modalHeaderClassName="bg-primary text-white"
            className="PickFromDocumentsModal"
            customFooter={(
                <div>
                    <NSButton text="Cancel" callback={handleCancel} outline />
                    <NSButton
                        className="ml-2"
                        color="primary"
                        text={`Attach${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
                        callback={handleConfirm}
                        disabled={selectedIds.length === 0}
                    />
                </div>
            )}
        >
            {availableDocuments.length === 0 ? (
                <div className="text-muted text-center py-4">
                    No documents available to attach.
                </div>
            ) : (
                <div className="PickFromDocumentsModal__list">
                    {availableDocuments.map(doc => {
                        const isSelected = selectedIds.includes(doc.id);
                        const fileIcon = getFileIcon({ fileFormat: doc.extension, isExpanded: false });

                        return (
                            <NSCard
                                key={doc.id}
                                className={classNames('NSCard--level-2 p-2 mt-2 mb-1', {
                                    'border border-primary': isSelected,
                                })}
                                onClick={() => handleToggleDocument(doc.id)}
                            >
                                <div className="d-flex align-items-center w-100">
                                    <div
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        role="presentation"
                                    >
                                        <NSCheckbox
                                            id={`document-select-${doc.id}`}
                                            checked={isSelected}
                                            callback={() => handleToggleDocument(doc.id)}
                                            iconOnly
                                        />
                                    </div>
                                    <NSIcon icon={fileIcon} className="text-white mx-2" />
                                    <div className="flex-grow-1 overflow-hidden">
                                        <div className="text-white text-truncate">{doc.name}</div>
                                        <div className="text-muted font-12 d-flex align-items-center">
                                            <NSIcon icon={IconFolder} className="mr-1 font-10" />
                                            <span className="text-truncate">{doc.directoryPath}</span>
                                        </div>
                                    </div>
                                </div>
                            </NSCard>
                        );
                    })}
                </div>
            )}
        </NSModal>
    );
};

export default PickFromDocumentsModal;
